import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useBilling } from "@/context/BillingContext";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, CreditCard, ExternalLink } from "lucide-react";

const inputCls =
  "w-full bg-white border border-[#E4DFD2] rounded-lg px-3 py-2 text-sm text-[#1F2326] placeholder-[#A9A294] focus:outline-none focus:border-[#1F2326] focus:ring-1 focus:ring-[#1F2326] transition-colors";

const useCheckoutPoll = (sessionId, onComplete) => {
  const [state, setState] = useState({ status: "pending", attempts: 0 });
  const stopRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return undefined;
    stopRef.current = false;
    let attempts = 0;

    const poll = async () => {
      if (stopRef.current) return;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        attempts += 1;
        setState({ status: r.data.payment_status, attempts });
        if (r.data.payment_status === "paid") {
          onComplete?.("paid", r.data);
          return;
        }
        if (r.data.status === "expired") {
          onComplete?.("expired", r.data);
          return;
        }
        if (attempts >= 10) {
          onComplete?.("timeout", r.data);
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        if (attempts >= 5) {
          onComplete?.("error", null);
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      stopRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  return state;
};

const SettingsPage = () => {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh: refreshBilling, subscription } = useBilling();
  const [portalLoading, setPortalLoading] = useState(false);

  const handleOpenPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/billing/portal", {
        return_url: window.location.origin + "/settings",
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open billing portal");
      setPortalLoading(false);
    }
  };

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    default_hourly_cost: 50,
    default_target_margin: 35,
    default_review_day: 28,
    currency: "USD",
  });
  const dirtyRef = useRef(false);

  const [checkoutBanner, setCheckoutBanner] = useState(null);

  useCheckoutPoll(sessionId, (state, data) => {
    if (state === "paid") {
      setCheckoutBanner({ tone: "success", message: `Plan activated. You are now on ${data?.plan_id ? data.plan_id.charAt(0).toUpperCase() + data.plan_id.slice(1) : "your new plan"}.` });
      refreshBilling();
      // Remove session_id from URL after success
      params.delete("session_id");
      setParams(params, { replace: true });
    } else if (state === "expired" || state === "timeout") {
      setCheckoutBanner({ tone: "info", message: "Checkout not completed. You can try again from the Pricing page." });
    } else if (state === "error") {
      setCheckoutBanner({ tone: "error", message: "Could not verify payment. Please refresh." });
    }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/settings");
        if (cancelled || dirtyRef.current) return;
        setForm(r.data);
      } catch {
        if (!cancelled) toast.error("Failed to load settings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setField = (key, value) => {
    dirtyRef.current = true;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", {
        company_name: form.company_name,
        default_hourly_cost: parseFloat(form.default_hourly_cost) || 0,
        default_target_margin: parseFloat(form.default_target_margin) || 0,
        default_review_day: parseInt(form.default_review_day, 10) || 28,
        currency: form.currency,
      });
      dirtyRef.current = false;
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSave} className="max-w-2xl space-y-6" data-testid="settings-page">
      {checkoutBanner ? (
        <div
          data-testid="checkout-banner"
          className={`rounded-xl px-4 py-3 text-sm border ${
            checkoutBanner.tone === "success"
              ? "bg-[#EFF3EE] border-[#C5D5C8] text-[#42624B]"
              : checkoutBanner.tone === "error"
              ? "bg-[#FAEEED] border-[#E7BFBA] text-[#7E332D]"
              : "bg-[#F0EADA] border-[#E4DFD2] text-[#7A5E1F]"
          }`}
        >
          <div className="flex items-start gap-2">
            {checkoutBanner.tone === "success" ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-none" /> : null}
            <span>{checkoutBanner.message}</span>
          </div>
        </div>
      ) : sessionId ? (
        <div className="rounded-xl px-4 py-3 text-sm border bg-[#F0EADA] border-[#E4DFD2] text-[#7A5E1F]">
          Verifying payment.
        </div>
      ) : null}

      <div className="bg-white border border-[#E4DFD2] rounded-xl p-8">
        <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C]">
          Workspace
        </div>
        <h2 className="font-display text-2xl font-semibold tracking-tight text-[#1F2326] mt-1">
          Defaults
        </h2>
        <p className="text-sm text-[#69747C] mt-1">
          Used for new clients and dashboard formatting.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-7">
          <div className="sm:col-span-2 flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-[#1F2326]">Company name</label>
            <input
              data-testid="settings-company-name"
              className={inputCls}
              value={form.company_name || ""}
              onChange={(e) => setField("company_name", e.target.value)}
              placeholder="Acme Studio"
              disabled={loading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-[#1F2326]">
              Default internal hourly cost
            </label>
            <input
              data-testid="settings-hourly-cost"
              type="number"
              min="0"
              className={inputCls}
              value={form.default_hourly_cost}
              onChange={(e) => setField("default_hourly_cost", e.target.value)}
              disabled={loading}
            />
            <div className="text-[11px] text-[#69747C]">Used as default for new clients.</div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-[#1F2326]">
              Default target margin (%)
            </label>
            <input
              data-testid="settings-target-margin"
              type="number"
              min="0"
              max="100"
              className={inputCls}
              value={form.default_target_margin}
              onChange={(e) => setField("default_target_margin", e.target.value)}
              disabled={loading}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-[#1F2326]">
              Default monthly review day
            </label>
            <input
              data-testid="settings-review-day"
              type="number"
              min="1"
              max="31"
              className={inputCls}
              value={form.default_review_day || 28}
              onChange={(e) => setField("default_review_day", e.target.value)}
              disabled={loading}
            />
            <div className="text-[11px] text-[#69747C]">Day of month for your retainer review. Auto-snapshot runs on this day.</div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-semibold text-[#1F2326]">Currency</label>
            <Select
              value={form.currency}
              onValueChange={(v) => setField("currency", v)}
              disabled={loading}
            >
              <SelectTrigger
                data-testid="settings-currency"
                className="w-full bg-white border-[#E4DFD2] rounded-lg text-sm"
              >
                <SelectValue placeholder="Select currency" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="USD">USD · US Dollar ($)</SelectItem>
                <SelectItem value="CAD">CAD · Canadian Dollar (C$)</SelectItem>
                <SelectItem value="GBP">GBP · British Pound (£)</SelectItem>
                <SelectItem value="AUD">AUD · Australian Dollar (A$)</SelectItem>
                <SelectItem value="NZD">NZD · New Zealand Dollar (NZ$)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <button
            type="submit"
            data-testid="settings-save-btn"
            disabled={saving || loading}
            className="bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] disabled:opacity-60 px-4 py-2 rounded-lg font-medium text-sm transition-colors"
          >
            {saving ? "Saving..." : "Save settings"}
          </button>
        </div>
      </div>

      {/* Billing card */}
      <div className="bg-white border border-[#E4DFD2] rounded-xl p-8" data-testid="billing-card">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-[#1F2326] flex items-center justify-center flex-none">
            <CreditCard className="w-4 h-4 text-[#B89B5E]" strokeWidth={2.25} />
          </div>
          <div className="flex-1">
            <div className="text-[11px] tracking-[0.08em] uppercase font-semibold text-[#69747C]">
              Billing
            </div>
            <h2 className="font-display text-2xl font-semibold tracking-tight text-[#1F2326] mt-1">
              {subscription?.plan_name ? `${subscription.plan_name} plan` : "Free trial"}
            </h2>
            <p className="text-sm text-[#69747C] mt-1">
              {subscription?.status === "active" && "You can update your card, view invoices, switch plans, or cancel any time."}
              {subscription?.status === "past_due" && "Your last payment failed. Update your card in the billing portal to keep your access."}
              {subscription?.status === "cancelled" && "Your subscription is cancelled. Pick a plan to resume."}
              {(!subscription || subscription?.status === "trialing" || subscription?.status === "trial_expired") &&
                "You're on the free trial. Pick a plan to keep your access after the trial ends."}
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-2">
              {subscription?.stripe_customer_id ? (
                <button
                  type="button"
                  onClick={handleOpenPortal}
                  disabled={portalLoading}
                  data-testid="manage-billing-btn"
                  className="inline-flex items-center gap-1.5 bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] disabled:opacity-60 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  {portalLoading ? "Opening." : "Manage billing"}
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              ) : subscription?.status === "active" ? (
                <div
                  data-testid="comp-account-note"
                  className="inline-flex items-center gap-1.5 bg-[#EFF3EE] text-[#42624B] border border-[#C5D5C8] px-3 py-2 rounded-lg text-sm font-medium"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Full access — granted by admin
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <div
                    data-testid="no-billing-account-note"
                    className="text-sm text-[#7A5E1F] bg-[#F0EADA] border border-[#E4DFD2] rounded-lg px-3 py-2"
                  >
                    No billing account found. Please choose a plan first.
                  </div>
                  <a
                    href="/pricing"
                    data-testid="choose-plan-btn"
                    className="inline-flex items-center gap-1.5 bg-[#1F2326] text-[#F8F6F1] hover:bg-[#2A2E32] px-4 py-2 rounded-lg text-sm font-medium transition-colors w-fit"
                  >
                    Choose a plan
                  </a>
                </div>
              )}
              <a
                href="/pricing"
                className="text-sm text-[#69747C] hover:text-[#1F2326] px-2 py-2"
                data-testid="view-pricing-link"
              >
                View all plans
              </a>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default SettingsPage;
